import { createNamespacedHelpers, mapActions } from 'vuex'
import MQTT from 'mqtt'
import Bugsnag from '@bugsnag/js'
import { EventBus } from './eventBus'
import { Howl } from 'howler'
import { sendNotification } from './notification'
import _ from 'lodash'

const adminController = createNamespacedHelpers('admin')
const conversationController = createNamespacedHelpers('conversation')
const filtersController = createNamespacedHelpers('inbox_filters')
const groupController = createNamespacedHelpers('group')

const INVALID_URL_TRY_AGAIN = 'Error URL no valida, por favor intente de nuevo'
const SENT = 'sent'

class SkNotification {
  constructor({ title, body } = {}) {
    this.title = title
    this.body = body
    this.sound = new Howl({
      src: [require('@/assets/sounds/chat-notification.mp3')]
    })
  }

  send() {
    this.sound.play()
    sendNotification(this.title, this.body)
  }

  sentEvent({ existConversation = false, author = '' } = {}) {
    if (author !== 'user' || !existConversation) return
    this.send()
  }
}

class SkCase {
  constructor({ admin, store, can, _case, message = {} }) {
    this.admin = admin
    this.store = store
    this.can = can
    this._case = _case
    this.message = message
  }

  setStore(commit, state) {
    this.store.commit(commit, state)

    return this
  }

  setCase() {
    return this.setStore('group/pushCase', this._case)
  }

  setGroupCase(canReadGroup, matchCase) {
    if (!canReadGroup) return this
    if (!matchCase) return this.setStore('group/removeCase', this._case)
    return this.setCase()
  }

  setAdmin(areSameAdmin, matchCase) {
    if (areSameAdmin && matchCase) return this.setCase()
    return this
  }

  isSameCase(sameCase, user, message) {
    if (!sameCase) return this

    this.setStore('case/pushMessage', message)

    if (this.message.author === 'user' && this.message.text) {
      const skNotification = new SkNotification({
        title: `Nuevo mensaje de ${user.first_name}`,
        body: this.message.text
      })

      skNotification.send()
    }

    return this
  }

  newAssignment(newAssignment, user) {
    if (!newAssignment) return this
    const skNotification = new SkNotification({
      title: 'Se te ha asignado un nuevo caso',
      body: `Seras el asesor de ${user.first_name}`
    })
    skNotification.send()
    return this
  }
}

export default {
  data () {
    return {
      client_connected: false,
      loading_url: false,
      errors_count: 0,
      channel: null,
      client_id: null,
      mqtt_url: null,
      chat_sound: null,
      case_sound: null,
      client: null
    }
  },
  computed: {
    ...adminController.mapState({
      admin: state => state.admin
    }),
    ...filtersController.mapState({
      selected_group_id: state => state.selected_group_id,
      filters: state => {
        return {
          unread: state.unread,
          box: state.box,
          search_text: state.search_text,
          platforms: state.platforms.map(el => el.name),
          labels: state.labels,
          admins: state.admins.map(el => el.admin_id),
          tags: state.tags,
          case_statuses: state.case_statuses,
          dates: state.dates
        }
      }
    }),
    ...conversationController.mapState({
      conversations: state => state.conversations
    }),
    ...groupController.mapState({
      cases: ({ cases }) => {
        return cases
      }
    }),
    isInChats () {
      return this.$route.name === 'Chats' || this.$route.name === 'Conversation'
    },
    isInCases () {
      return this.$route.name === 'Cases' || this.$route.name === 'Case'
    },
    isInWebhooks () {
      return this.$route.name === 'Webhooks' || this.$route.name === 'Webhook'
    }
  },
  methods: {
    ...mapActions(['inbox/client', 'bmessaging/get_url', 'case/get']),
    getInboxClient() {
      if (this.client && this.client.connected) return

      this['inbox/client']({
        account_id: this.$route.params.account_id,
        callback: res => {
          if (res.error) {
            console.error('GETTING CLIENT ERROR')

            this.client_connected = false
          }

          this.channel = res.resource.channel
          this.client_id = res.resource.client_id
          this.mqtt_url = res.resource.url
          this.createClient()
        }
      })
    },
    createClient() {
      try {
        const self = this

        if (!self.mqtt_url) {
          Bugsnag.leaveBreadcrumb('Error URL no valida, por favor intente de nuevo')

          this.client_connected = false

          return this.$message.error('Error URL no valida, por favor intente de nuevo')
        }

        Bugsnag.leaveBreadcrumb('CONNECT TO WS URL', { url: self.mqtt_url })

        this.client = MQTT.connect(self.mqtt_url, {
          protocolId: 'MQIsdp',
          protocolVersion: 3,
          clientId: self.client_id,
          keepalive: 300,
          reconnectPeriod: 2000,
          username: `${this.getCurrentUser().email}`,
          transformWsUrl: () => {
            self.errors_count++

            if (self.errors_count >= 4 && !self.loading_url) self.getNewURL()

            Bugsnag.leaveBreadcrumb('TRANSFORM WS URL', { url: self.mqtt_url })

            if (self.mqtt_url) return self.mqtt_url

            this.client_connected = false

            Bugsnag.leaveBreadcrumb('INVALID URL')
          }
        })

        //this.client._hand

        if (!this.client) {
          this.client_connected = false

          return this.$message.error('Error URL no valida, por favor intente de nuevo')
        }

        this.setMQTTListeners()
      } catch (e) {
        this.client_connected = false

        console.error('CREATE CLIENT ERROR', e)
      }
    },
    getNewURL() {
      this.loading_url = true
      const self = this
      self['bmessaging/get_url']({
        params: { client_id: self.client_id },
        callback: res => {
          self.loading_url = false
          if (res.error) {
            self.$message.error(res.error)
            console.error('Error renovando la url del cliente')
            self.mqtt_url = ''
          } else {
            self.mqtt_url = res.url
            self.errors_count = 0
          }
        }
      })
    },
    setMQTTListeners() {
      this.client.on('connect', this.connectClient)
      this.client.on('message', this.handleMessage)
      this.client.on('error', function () { console.error('CLIENT ON ERROR') })
    },
    connectClient() {
      const self = this

      this.client.subscribe(this.channel, { qos: 0 }, (error) => {
        if (error) {
          self.client_connected = false

          return console.log('%c Error conectando con servicio en tiempo real.', 'color:#ff0000')
        }

        console.log('%c Conectado con servicio en tiempo real.', 'color:#00ff00')

        return setTimeout(() => { self.client_connected = true }, 300)
      })
    },
    handleMessage (topic, data) {
      try {
        const msg = JSON.parse(data.toString())
        let { message, conversation, type, message_id, conversation_id } = msg
        const { channel, isInChats, isInCases, isInWebhooks, admin = {} } = this

        const isSpecialType = [
          `adminAccount_${admin.admin_id}`,
          'case_notification',
          'notification',
          'received',
          'sent'
        ].includes(type)

        const canCatch = [
          isInCases,
          isInChats,
          isSpecialType,
          isInWebhooks
        ].some(Boolean)

        if (topic !== channel || !canCatch) return

        const { case: _case = {}, case_id: caseId } = msg
        const { User = {} } = _case

        if (!message && conversation) message = conversation

        const is_same_conversation = this.sameConversation(msg)
        const exist_conversation = this.conversations.find(({ _id }) => {
          return _id === msg.conversation_id
        })
        const is_same_case = this.sameCase(msg)
        const exist_case = this.cases[msg.case_id]

        switch (type) {
          case 'received':
          case 'sent':
            {
              const notificationTitle = User.first_name
                ? `Nuevo mensaje de ${User.first_name}`
                : caseId
                  ? `Nuevo mensaje del caso ${caseId}`
                  : 'Nuevo mensaje'

              const skNotification = new SkNotification({
                title: notificationTitle,
                body: message.text
              })

              const existConversation = !_.isEmpty(exist_conversation || exist_case)

              if (this.isInChats) {
                if (this.matchChatFilter(conversation)) {
                  this.$store.commit('conversation/moveConversationToTop', msg)
                }

                if (is_same_conversation) {
                  let conversationRead = {
                    _id: conversation._id,
                    key: 'read',
                    value: true
                  }

                  let conversationLastMessagePreview = {
                    _id: conversation._id,
                    key: 'last_message_preview',
                    value: message.text
                  }

                  let conversationLastMessageTime = {
                    _id: conversation._id,
                    key: 'last_message_time',
                    value: conversation.last_message_time
                  }

                  this.$store.commit('conversation/pushMessage', message)
                  this.$store.commit('conversation/updateConversationData', conversationRead)
                  this.$store.commit('conversation/updateConversationData', conversationLastMessagePreview)
                  this.$store.commit('conversation/updateConversationData', conversationLastMessageTime)
                }
              } else if (this.isInCases && is_same_case) {
                message._id = message_id

                if (type === SENT) message.status = SENT

                let caseUpdatedAt = {
                  case_id: caseId,
                  key: 'updatedAt',
                  value: conversation.updatedAt
                }

                let caseLastMessagePreview = {
                  case_id: caseId,
                  key: 'last_message_preview',
                  value: message.text
                }

                this.$store.commit('group/updateCaseData', caseUpdatedAt)
                this.$store.commit('group/updateCaseData', caseLastMessagePreview)
                this.$store.commit('case/pushMessage', message)

                skNotification.sentEvent({
                  existConversation,
                  author: message.author
                })
              }
            }
            break
          case 'conversation_updated':
            if (!this.isInChats) break
            if (this.matchChatFilter(conversation)) {
              if (is_same_conversation) EventBus.$emit('conversation/updated', message)
              break
            }

            //this.$store.commit('conversation/removeConversation', conversation_id)
            //if (is_same_conversation) this.$router.push({ name: 'Chats' }).then(() => {})
            break
          case 'case_notification': // TODO: check notification implemention
            if (this.isInChats && conversation) {
              if (this.matchChatFilter(conversation)) {
                this.$store.commit('conversation/moveConversationToTop', msg)
              }

              if (is_same_conversation) {
                this.$store.commit('conversation/pushMessage', message)
                this.$store.commit('conversation/updateConversationData', {
                  _id: conversation._id,
                  key: 'read',
                  value: true
                })
              }
            }
            if (!_case) return
            const canReadGroup =
              this.$can('read', 'case-group') //&& this.selected_group_id
            const areSameAdmin = _case.admin_assigned_id == this.admin.admin_id
            const matchCase = this.matchCaseFilter(_case)
            const currentCase = new SkCase({
              admin: this.admin,
              _case,
              store: this.$store,
              can: this.$can
            })
            EventBus.$emit('groups/change')
            currentCase
              .setGroupCase(canReadGroup, matchCase)
              .setAdmin(areSameAdmin, matchCase)
              .isSameCase(is_same_case, User, message)
            if (!message.event) break
            const { payload = {}, name } = message.event
            if (is_same_case && name !== 'read_change') this.getCase(_case)

            switch (name) {
              case 'assigment':
                {
                  const isNewAssigment =
                    payload.to == this.admin.admin_id &&
                    _case.status !== 'close'
                  currentCase.newAssignment(isNewAssigment, User)
                  let existCase = this.existCase(currentCase._case.case_id)
                  if(existCase){
                    this.$store.dispatch('admin/countTodayCases', {
                      admin_id: this.admin.admin_id,
                      params: { account_id: currentCase._case.account_id }
                    }).then()
                  }
                  const isSameAdmin = _case.admin_assigned_id == this.admin.admin_id
                  if (this.$can('read', 'case-group') || isSameAdmin) break
                  this.$store.commit('group/removeCase', _case)
                  this.closeCaseView(is_same_case)
                }
                break
              case 'status_change':
                if (payload.to !== 'close') break
                this.$store.commit('group/removeCase', _case)
                this.closeCaseView(is_same_case)
                EventBus.$emit('groups/count')
                break
              case 'derivation':
                EventBus.$emit('groups/count')
                break
            }
            break
          case 'webhookLog': // catch webhook incoming log
            const { log } = msg
            EventBus.$emit('webhook/log', log)
            break
          case 'messageUpdated': // update timeline when message status is updated
            if (!this.sameCase(message)) return

            setTimeout(() => { EventBus.$emit('message/updated', message) }, 1000)

            break
          case 'caseHook': // update cases summary
            const { case: skCase } = msg
            const { admin_assigned_id, account_id } = skCase ? skCase : {}
            if(skCase) EventBus.$emit('case/new', { admin_assigned_id, account_id })            
            break
          case 'notification': // catch notification
            const { notification, admin: Admin = {} } = msg

            const { admin_id: adminId } = admin || {}
            if (Admin !== adminId) return

            EventBus.$emit('notification/new', notification)
            this.$message.info('Nueva notificación')
            if (notification.type === 'case') return

            const { title, description: body } = notification
            const skNotification = new SkNotification({ title, body })

            skNotification.send()
            break
          case `adminAccount_${this.admin.admin_id}`:
            const { admin } = msg

            if (this.admin.admin_id == admin.admin_id) {
              this.$store.commit('admin/updateAdminStatus', admin.status)
            }

            EventBus.$emit(
              'accountAdmin/statusUpdated',
              { admin_id: admin.admin_id, status: admin.status }
            )
            break
        }
      } catch (e) {
        console.error(e)
      }
    },
    getCase (_case) {
      if (!_case) return
      this.$store.commit('case/updating')
      const self = this

      this['case/get']({
        case_id: _case.case_id,
        params: { account_id: self.$route.params.account_id },
        soft: true,
        callback: res => {
          self.$store.commit('case/updating', false)
          if (res.error) {
            self.$message.error(res.error)
          }
        }
      })
    },
    closeCaseView (is_same_case) {
      if (is_same_case /* && !this.$can('read', 'stay-in-case') */) {
        this.$router.push({ name: 'Cases' }).then(() => {}).catch(()=>{});
      }
    },
    sameConversation ({ conversation_id }) {
      return this.$route.params.conversation_id && conversation_id && conversation_id === this.$route.params.conversation_id
    },
    sameCase ({ case_id }) {
      return this.$route.params.case_id && case_id && case_id == this.$route.params.case_id
    },
    existCase(caseId) {
      if (!caseId) return false
      const caseExists = this.cases[caseId]
      
      return caseExists
    },
    matchChatFilter ({ _id = null, platform, admin_ids, box } = {}) {
      if (this.filters.platforms.length && !this.filters.platforms.includes(platform)) {
        return false
      }
      if (this.filters.search_text.length && !this.conversations.find(el => el._id === _id)) {
        return false
      }
      if (this.filters.admins.length && admin_ids.length && !this.filters.admins.filter(el => admin_ids.includes(el)).length) {
        return false
      }
      if (this.filters.box !== box) {
        return false
      }

      return true
    },
    matchCaseFilter ({ case_id, group_id, status, admin_assigned_id, platform }) {
      if (this.filters.case_statuses.length && !this.filters.case_statuses.includes(status)) {
        return false
      } else if (!this.filters.case_statuses.length && status !== 'open') {
        return false
      }
      
      if (this.filters.search_text.length && !this.cases[case_id]) {
        return false
      }
      
      if (this.filters.admins.length && admin_assigned_id && !this.filters.admins.includes(admin_assigned_id)) {
        return false
      }
      if (this.filters.platforms.length && !this.filters.platforms.includes(platform)) {
        return false
      }
      if(!group_id) {
        return !this.selected_group_id
      } else if (this.selected_group_id && group_id != this.selected_group_id) {
        return false
      }

      return true
    },
    playChatSound () {
      if (!this.chat_sound) {
        this.chat_sound = new Howl({
          src: [require('@/assets/sounds/chat-notification.mp3')]
        })
      }
      this.chat_sound.play()
    },
    playCaseSound () {
      if (this.admin && this.admin.status !== 'available') return
      if (!this.case_sound) {
        this.case_sound = new Howl({
          src: [require('@/assets/sounds/case-notification.mp3')]
        })
      }
      this.case_sound.play()
    }
  },
  mounted() {
    this.getInboxClient()
  },
  watch: {
    client_connected(value, oldValue) {
      if (value !== oldValue) this.$store.commit('inbox/updateClientConnected', value)
    }
  },
  beforeDestroy () {
    if (this.client) {
      this.client.off('connect', this.connectClient)
      this.client.off('message', this.handleMessage)
    }
  }
}
